import { useEffect, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Shared";
import { fmtCur, fmtDate, fmtDateTime } from "@/lib/format";
import HealthBadge from "@/components/HealthBadge";
import DocumentHealthPills from "@/components/DocumentHealthPills";
import EmployeeSignaturePad from "@/components/EmployeeSignaturePad";
import EmbeddedDocuments from "@/components/EmbeddedDocuments";
import EmployeeFormWizard, { EMPLOYEE_EMPTY } from "@/components/EmployeeFormWizard";
import VehiclePicker from "@/components/VehiclePicker";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { ArrowLeft, Plus, Trash2, Pencil, X, Phone, Mail, MapPin, AlertOctagon, AlertTriangle, Camera, FileDown, ChevronDown, ChevronRight, Paperclip, CreditCard, KeyRound, Truck, AlertCircle, CalendarDays, CalendarPlus, Smartphone, Copy, Check, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import SignatureCanvas from "@/components/SignatureCanvas";
import DepartmentPicker, { DepartmentBadge } from "@/components/DepartmentPicker";
import EmployeeHrTab from "@/components/EmployeeHrTab";
import EmployeeAdvancesTab from "@/components/EmployeeAdvancesTab";
import OffboardingWizard from "@/components/OffboardingWizard";
import HrEmployeeWidget from "@/components/HrEmployeeWidget";
import { toast } from "sonner";
import { compressImage, fmtBytes } from "@/lib/imageCompressor";

const TABS = [
  "Übersicht", "Stammdaten", "Portal-Zugang", "Dokumente", "Urlaub & Krankenstand",
  "Zeiterfassung", "Lenkprotokoll", "Dienstplan", "Kalender",
  "Strafen", "Akonto/Bonus", "Verträge", "Karten", "Fahrzeughistorie", "Notizen", "Audit",
];

export default function MitarbeiterProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canEdit = can("mitarbeiter", "edit");
  const canExport = can("mitarbeiter", "export");
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("Übersicht");
  const [photoTs, setPhotoTs] = useState(Date.now());
  const [timeEntries, setTimeEntries] = useState([]);
  const [drivingLogs, setDrivingLogs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [showOffboarding, setShowOffboarding] = useState(false);

  async function reloadShifts() {
    const { data } = await api.get("/shifts", { params: { employee_id: id } });
    setShifts(data);
  }

  async function reloadBonuses() {
    const { data } = await api.get("/bonuses", { params: { employee_id: id } });
    setBonuses(data);
  }
  async function reloadDocs() {
    const { data } = await api.get("/documents", { params: { owner_type: "employee", owner_id: id } });
    setDocs(data);
  }

  useEffect(() => {
    api.get(`/employees/${id}`).then(({ data }) => setEmp(data));
    api.get("/time-entries", { params: { employee_id: id } }).then(({ data }) => setTimeEntries(data));
    api.get("/driving-logs", { params: { employee_id: id } }).then(({ data }) => setDrivingLogs(data));
    api.get("/documents", { params: { owner_type: "employee", owner_id: id } }).then(({ data }) => setDocs(data));
    api.get("/shifts", { params: { employee_id: id } }).then(({ data }) => setShifts(data));
    api.get("/audit").then(({ data }) => setAudit(data.filter((a) => a.record_id === id)));
    api.get("/handovers", { params: { employee_id: id } }).then(({ data }) => setHandovers(data));
    reloadBonuses();
  }, [id]);

  if (!emp) return <div className="p-8 text-zinc-500">Lade Profil...</div>;

  // Alert calculation
  const today = new Date().toISOString().slice(0, 10);
  const expiredDocs = docs.filter((d) => d.doc_status === "abgelaufen");
  const expiringDocs = docs.filter((d) => d.doc_status === "bald");
  const openPenalties = bonuses.filter((b) => b.type === "strafe" && b.status === "offen");
  const upcomingAbsences = shifts.filter(
    (s) => s.date >= today && ["urlaub", "krank", "frei"].includes(s.shift_type)
  ).slice(0, 3);
  const contracts = docs.filter((d) => /vertrag|contract/i.test(d.doc_type || ""));
  const absences = shifts.filter((s) => s.shift_type && s.shift_type !== "arbeit");

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const hasPhoto = !!emp.profile_image_path;
  const photoUrl = hasPhoto ? `${API_URL}/api/employees/${id}/photo?t=${photoTs}` : null;

  async function uploadPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Nur Bilder erlaubt"); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error("Max. 20 MB"); return; }
    try {
      // Phase 5Z.42 — compress to ≤1920px before upload (saves storage costs)
      const original = f;
      const compressed = await compressImage(f, { maxDimension: 1920, jpegQuality: 0.85 });
      if (compressed !== original && compressed.size < original.size) {
        toast.success(`Foto komprimiert: ${fmtBytes(original.size)} → ${fmtBytes(compressed.size)}`);
      }
      const fd = new FormData();
      fd.append("file", compressed);
      await api.post(`/employees/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto hochgeladen");
      const { data } = await api.get(`/employees/${id}`);
      setEmp(data);
      setPhotoTs(Date.now());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    }
    const input = e.target;
    if (input) input.value = "";
  }

  async function removePhoto() {
    if (!window.confirm("Foto wirklich entfernen?")) return;
    await api.delete(`/employees/${id}/photo`);
    toast.success("Foto entfernt");
    const { data } = await api.get(`/employees/${id}`);
    setEmp(data);
    setPhotoTs(Date.now());
  }

  return (
    <div className="p-6 md:p-8" data-testid="employee-profile">
      <Link to="/mitarbeiter" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-200 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Zurück zur Liste
      </Link>

      {/* HEADER CARD */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 mb-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-5">
            <div className="relative group shrink-0">
              <div className="w-20 h-20 rounded-full bg-blue-600/20 border border-blue-600/30 flex items-center justify-center text-blue-400 font-display text-2xl font-semibold overflow-hidden">
                {hasPhoto ? (
                  <img src={photoUrl} alt={`${emp.first_name} ${emp.last_name}`} className="w-full h-full object-cover" data-testid="emp-photo"/>
                ) : (
                  (emp.first_name?.[0] || "?") + (emp.last_name?.[0] || "")
                )}
              </div>
              {canEdit && (
                <>
                  <label title="Foto ändern" className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity" data-testid="emp-photo-upload-label">
                    <Camera className="w-6 h-6 text-white"/>
                    <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="emp-photo-input"/>
                  </label>
                  {hasPhoto && (
                    <button onClick={removePhoto} title="Foto entfernen" data-testid="emp-photo-remove"
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border-2 border-zinc-900">
                      <X className="w-3 h-3"/>
                    </button>
                  )}
                </>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Mitarbeiter</div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <h1 className="font-display text-2xl md:text-3xl font-bold text-zinc-50">{emp.first_name} {emp.last_name}</h1>
                {emp.status === "inaktiv" ? (
                  <span
                    data-testid="emp-registration-badge-abgemeldet"
                    title="Mitarbeiter ist abgemeldet (ausgetreten)"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-500/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Abgemeldet
                  </span>
                ) : (
                  <span
                    data-testid="emp-registration-badge-angemeldet"
                    title="Mitarbeiter ist angemeldet (aktiv im Unternehmen)"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Angemeldet
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusBadge status={emp.status} />
                {emp.health_level && <HealthBadge level={emp.health_level} reasons={emp.health_reasons || []} size="xs" testid="emp-profile-health"/>}
                <span className="text-xs text-zinc-400 capitalize">{emp.role}</span>
                {emp.department && <><span className="text-zinc-600">·</span><span className="text-xs text-zinc-400">{emp.department}</span></>}
                {emp.default_workplace && <><span className="text-zinc-600">·</span><span className="text-xs text-zinc-400 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{emp.default_workplace}</span></>}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400 flex-wrap">
                {emp.phone && <a href={`tel:${emp.phone}`} className="font-mono inline-flex items-center gap-1 hover:text-zinc-200"><Phone className="w-3 h-3"/>{emp.phone}</a>}
                {emp.email && <a href={`mailto:${emp.email}`} className="font-mono inline-flex items-center gap-1 hover:text-zinc-200"><Mail className="w-3 h-3"/>{emp.email}</a>}
              </div>
              <DocumentHealthPills employee={emp} />
            </div>
          </div>

          {/* ALERT BADGES */}
          <div className="flex flex-col items-end gap-2">
            {canEdit && (
              <button
                onClick={() => setShowAbsenceForm(true)}
                data-testid="emp-absence-request-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600/80 hover:bg-amber-600 text-white rounded font-medium"
              >
                <CalendarPlus className="w-3.5 h-3.5"/> Abwesenheit anfragen
              </button>
            )}
            {canEdit && emp?.status !== "abgemeldet" && (
              <button
                onClick={() => setShowOffboarding(true)}
                data-testid="emp-offboarding-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-rose-700/80 hover:bg-rose-700 text-white rounded font-medium"
                title="3-Schritt-Wizard: Stichtag · Settlement · Bestätigen"
              >
                Offboarding starten
              </button>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              {expiredDocs.length > 0 && <AlertBadge tone="critical" icon={AlertOctagon} count={expiredDocs.length} label="Abgelaufene Dokumente"/>}
              {expiringDocs.length > 0 && <AlertBadge tone="warning" icon={AlertTriangle} count={expiringDocs.length} label="Dokumente bald fällig"/>}
              {openPenalties.length > 0 && <AlertBadge tone="critical" icon={AlertOctagon} count={openPenalties.length} label="Offene Strafen"/>}
              {upcomingAbsences.length > 0 && <AlertBadge tone="info" icon={AlertTriangle} count={upcomingAbsences.length} label="Bevorstehende Abwesenheit"/>}
              {expiredDocs.length === 0 && expiringDocs.length === 0 && openPenalties.length === 0 && upcomingAbsences.length === 0 && (
                <span className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded">Alles ok</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* HR-Widget — Resturlaub, Überstunden, Krank YTD, Warnungen */}
      <HrEmployeeWidget employeeId={id} className="mb-6"
        onEditCarryOver={() => setTab("Urlaub & Krankenstand")} />

      {/* TAB BAR */}
      <div className="border-b border-zinc-800 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`profile-tab-${t}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t ? "text-zinc-50 border-blue-600" : "text-zinc-400 hover:text-zinc-200 border-transparent"
              }`}>{t}</button>
          ))}
        </div>
      </div>

      {tab === "Übersicht" && <OverviewTab emp={emp} empId={id} canExport={canExport}/>}
      {tab === "Stammdaten" && <StammdatenTab emp={emp} canEdit={canEdit} onSaved={(updated) => setEmp(updated)}/>}
      {tab === "Portal-Zugang" && <PortalAccessTab emp={emp} canEdit={canEdit} />}
      {tab === "Dokumente" && <EmbeddedDocuments ownerType="employee" ownerId={id} ownerLabel={`${emp.first_name} ${emp.last_name}`} onChange={reloadDocs}/>}
      {tab === "Urlaub & Krankenstand" && <EmployeeHrTab empId={id} emp={emp} canEdit={canEdit} />}
      {tab === "Zeiterfassung" && <ExportableMiniTable rows={timeEntries} columns={[["date", "Datum"], ["work_start", "Von"], ["work_end", "Bis"], ["work_minutes", "Arbeit (min)"], ["total_km", "KM"], ["status", "Status"]]} empId={id} emp={emp} reportKind="time" reportLabel="Zeiterfassung" canExport={canExport}/>}
      {tab === "Lenkprotokoll" && <ExportableMiniTable rows={drivingLogs} columns={[["date", "Datum"], ["driving_start", "Lenk Von"], ["driving_end", "Lenk Bis"], ["driving_minutes", "Lenkzeit (min)"], ["total_km", "KM"]]} empId={id} emp={emp} reportKind="driving" reportLabel="Lenkprotokoll" canExport={canExport}/>}
      {tab === "Dienstplan" && <MiniTable rows={shifts} columns={[["date", "Datum"], ["shift_type", "Typ"], ["workplace", "Ort"], ["notes", "Notizen"]]}/>}
      {tab === "Kalender" && <KalenderTab empId={id} shifts={shifts} timeEntries={timeEntries} handovers={handovers} onRequestAbsence={() => setShowAbsenceForm(true)}/>}
      {tab === "Strafen" && <BonusTab employeeId={id} items={bonuses.filter((b) => b.type === "strafe")} onChange={reloadBonuses} defaultType="strafe" canEdit={canEdit} canDelete={can("mitarbeiter", "delete")} canCreate={can("mitarbeiter", "create")}/>}
      {tab === "Akonto/Bonus" && <EmployeeAdvancesTab empId={id} emp={emp} canEdit={canEdit} />}
      {tab === "Verträge" && <EmbeddedDocuments ownerType="contract" ownerId={id} ownerLabel={`${emp.first_name} ${emp.last_name}`} title="Vertrag"/>}
      {tab === "Karten" && <KartenTab empId={id} empLabel={`${emp.first_name} ${emp.last_name}`} canEdit={canEdit}/>}
      {tab === "Fahrzeughistorie" && <FahrzeughistorieTab empId={id} emp={emp} handovers={handovers} onChange={() => api.get("/handovers", { params: { employee_id: id } }).then(({ data }) => setHandovers(data))}/>}
      {tab === "Notizen" && <NotizenTab emp={emp} canEdit={canEdit} onChange={() => api.get(`/employees/${id}`).then(({ data }) => setEmp(data))}/>}
      {tab === "Audit" && <MiniTable rows={audit} columns={[["timestamp", "Zeit"], ["action", "Aktion"], ["module", "Modul"], ["user_name", "Benutzer"]]}/>}

      {showAbsenceForm && (
        <AbsenceRequestModal
          empId={id}
          onClose={() => setShowAbsenceForm(false)}
          onSaved={() => { reloadShifts(); setShowAbsenceForm(false); setTab("Abwesenheiten"); }}
        />
      )}

      {showOffboarding && (
        <OffboardingWizard
          empId={id}
          empName={`${emp?.first_name || ""} ${emp?.last_name || ""}`.trim()}
          onClose={() => setShowOffboarding(false)}
          onDone={() => {
            setShowOffboarding(false);
            // Reload employee + tab data to reflect the new status.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

function AlertBadge({ tone, icon: Icon, count, label }) {
  const colors = {
    critical: "bg-red-500/10 text-red-400 border-red-500/30",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    info: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  };
  return (
    <div className={`px-3 py-1.5 text-xs border rounded-md inline-flex items-center gap-2 ${colors[tone]}`} data-testid={`alert-badge-${tone}`}>
      <Icon className="w-3.5 h-3.5"/>
      <span className="font-mono font-bold">{count}</span>
      <span>{label}</span>
    </div>
  );
}

function OverviewTab({ emp, empId, canExport }) {
  return (
    <div className="space-y-5">
      <MiniStatsRow empId={empId}/>
      <MonthlySummaryTab empId={empId} canExport={canExport}/>
    </div>
  );
}

function MiniStatsRow({ empId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      const to = now.toISOString().slice(0, 10);
      try {
        const [t, d, b, h] = await Promise.all([
          api.get("/time-entries", { params: { employee_id: empId, from, to } }),
          api.get("/driving-logs", { params: { employee_id: empId, from, to } }),
          api.get("/bonuses", { params: { employee_id: empId } }),
          api.get("/handovers", { params: { employee_id: empId } }),
        ]);
        const workMin = t.data.reduce((s, x) => s + (x.work_minutes || 0), 0);
        const km = d.data.reduce((s, x) => s + (x.total_km || 0), 0);
        const bonus = b.data.filter(x => x.date >= from && x.date <= to)
          .reduce((s, x) => s + (x.type === "bonus" ? +x.amount : -(+x.amount || 0)), 0);
        const damages = h.data.filter(x => x.damages_noted && x.damages_noted.length > 0).length;
        setStats({ hours: (workMin / 60).toFixed(1), km, bonus, damages });
      } catch { setStats({ hours: 0, km: 0, bonus: 0, damages: 0 }); }
    })();
  }, [empId]);

  if (!stats) return <div className="text-zinc-500 text-sm">Lade Monatsstatistik…</div>;

  const Tile = ({ label, value, color, suffix = "" }) => (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
      <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${color}`}>
        {value}<span className="text-sm text-zinc-500 ml-1 font-normal">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mini-stats">
      <Tile label="Diesen Monat" value={stats.hours} suffix="h" color="text-blue-400"/>
      <Tile label="Gefahrene KM" value={stats.km.toLocaleString("de-AT")} suffix="km" color="text-emerald-400"/>
      <Tile label="Bonus / Abzug" value={`€ ${stats.bonus.toFixed(0)}`} color={stats.bonus >= 0 ? "text-emerald-400" : "text-red-400"}/>
      <Tile label="Schäden gesamt" value={stats.damages} color={stats.damages > 0 ? "text-red-400" : "text-zinc-400"}/>
    </div>
  );
}

// ============================================================
// PortalAccessTab — manage Fahrer-Portal credentials (Phase 5Z.9)
// ============================================================
function PortalAccessTab({ emp, canEdit }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState(null);  // { username, password } shown once
  const [copied, setCopied] = useState(false);

  const isFahrer = emp?.role === "fahrer";
  const portalUrl = `${window.location.origin}/fahrer/login`;

  async function loadStatus() {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${emp.id}/portal-status`);
      setStatus(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { if (emp?.id) loadStatus(); /* eslint-disable-next-line */ }, [emp?.id]);

  async function enable() {
    if (!window.confirm(`Fahrer-Portal-Zugang für ${emp.first_name} ${emp.last_name} aktivieren?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${emp.id}/enable-portal`, {});
      setCredentials({ username: data.username, password: data.initial_password });
      toast.success("Portal aktiviert — Zugangsdaten anzeigen!");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktivieren fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function reset() {
    if (!window.confirm("Passwort zurücksetzen? Das alte Passwort verliert sofort die Gültigkeit.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${emp.id}/reset-portal-password`, {});
      setCredentials({ username: data.username, password: data.new_password });
      toast.success("Passwort zurückgesetzt");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function disable() {
    if (!window.confirm("Portal-Zugang deaktivieren? Der Fahrer kann sich nicht mehr anmelden.")) return;
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/disable-portal`, {});
      toast.success("Portal deaktiviert");
      setCredentials(null);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deaktivieren fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function copyAll() {
    if (!credentials) return;
    const text = `Fahrer-Portal Zugangsdaten\nURL: ${portalUrl}\nBenutzername: ${credentials.username}\nPasswort: ${credentials.password}\n\nDas Passwort muss beim ersten Login geändert werden.`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("In die Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
    }
  }

  if (!isFahrer) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 text-center" data-testid="portal-access-not-fahrer">
        <Smartphone className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
        <p className="text-sm text-zinc-400">Das Fahrer-Portal ist nur für Mitarbeiter mit Rolle <strong className="text-zinc-200">„Fahrer"</strong> verfügbar.</p>
      </div>
    );
  }

  if (loading) return <div className="text-zinc-500 text-sm">Lade…</div>;

  return (
    <div className="space-y-4" data-testid="portal-access-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
        <div className="flex items-start gap-3">
          <Smartphone className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-zinc-100">Fahrer-Portal</h3>
            <p className="text-xs text-zinc-400 mt-1">
              Mobiles Portal mit eigenem Login für den Fahrer. Mit Zugriff auf Profil, Lebenslauf und (folgend) Zeiterfassung, Schichtplan, Fahrzeug-Übergabe.
            </p>
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 w-32">Status:</span>
                {status?.portal_enabled
                  ? <span className="px-2 py-0.5 bg-emerald-500/15 text-emerald-300 rounded font-medium" data-testid="portal-status-active">Aktiv</span>
                  : <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded" data-testid="portal-status-inactive">Inaktiv</span>}
              </div>
              {status?.username && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-32">Benutzername:</span>
                  <span className="font-mono text-zinc-200" data-testid="portal-username-display">{status.username}</span>
                </div>
              )}
              {status?.last_portal_login_at && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-32">Letzter Login:</span>
                  <span className="font-mono text-zinc-300">{fmtDateTime(status.last_portal_login_at)}</span>
                </div>
              )}
              {status?.portal_enabled && status?.must_change_password && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-32">Erst-Login ausstehend:</span>
                  <span className="text-amber-300">⏳ noch nicht geändert</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 w-32">Login-URL:</span>
                <span className="font-mono text-blue-300 text-[11px]">{portalUrl}</span>
              </div>
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap gap-2">
            {!status?.portal_enabled ? (
              <button
                onClick={enable}
                disabled={busy}
                data-testid="portal-enable-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 text-white text-sm font-medium rounded"
              >
                <ShieldCheck className="w-4 h-4" /> Portal aktivieren
              </button>
            ) : (
              <>
                <button
                  onClick={reset}
                  disabled={busy}
                  data-testid="portal-reset-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white text-sm font-medium rounded"
                >
                  <RefreshCw className="w-4 h-4" /> Passwort zurücksetzen
                </button>
                <button
                  onClick={disable}
                  disabled={busy}
                  data-testid="portal-disable-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/80 hover:bg-red-700 disabled:bg-zinc-800 text-white text-sm font-medium rounded"
                >
                  <ShieldOff className="w-4 h-4" /> Deaktivieren
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Credentials shown once */}
      {credentials && (
        <div className="bg-amber-500/5 border-2 border-amber-500/50 rounded-md p-5" data-testid="portal-credentials-box">
          <div className="flex items-start gap-3">
            <KeyRound className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-200">Zugangsdaten — Bitte JETZT kopieren!</h4>
              <p className="text-xs text-zinc-300 mt-1">
                Das Passwort wird <strong>nur einmal</strong> angezeigt. Sende es zusammen mit dem Link an den Fahrer.
                Beim ersten Login muss er es ändern.
              </p>
              <div className="mt-3 bg-zinc-950 border border-zinc-800 rounded p-3 font-mono text-xs space-y-1">
                <div><span className="text-zinc-500">URL:</span> <span className="text-blue-300">{portalUrl}</span></div>
                <div><span className="text-zinc-500">Benutzername:</span> <span className="text-zinc-100" data-testid="portal-cred-username">{credentials.username}</span></div>
                <div><span className="text-zinc-500">Passwort:</span> <span className="text-amber-200 font-bold" data-testid="portal-cred-password">{credentials.password}</span></div>
              </div>
              <button
                onClick={copyAll}
                data-testid="portal-cred-copy"
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-medium rounded"
              >
                {copied ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Kopiert!</> : <><Copy className="w-3.5 h-3.5" /> Alle Zugangsdaten kopieren</>}
              </button>
              <button
                onClick={() => setCredentials(null)}
                data-testid="portal-cred-dismiss"
                className="ml-2 text-xs text-zinc-500 hover:text-zinc-300 underline"
              >
                Habe kopiert — verbergen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver signature — used by Auto-Generator for every committed entry */}
      <EmployeeSignaturePad
        employeeId={emp.id}
        employeeName={`${emp.first_name} ${emp.last_name}`}
        initialSignature={emp.signature_data_url || null}
      />
    </div>
  );
}

function StammdatenTab({ emp, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...EMPLOYEE_EMPTY, ...emp });

  function openEdit() {
    setForm({ ...EMPLOYEE_EMPTY, ...emp, login_password: "" });
    setEditing(true);
  }

  async function handleSave() {
    try {
      const { data } = await api.put(`/employees/${emp.id}`, form);
      toast.success("Stammdaten gespeichert");
      onSaved?.(data);
      setEditing(false);
    } catch {
      toast.error("Fehler beim Speichern");
    }
  }

  const sections = [
    {
      title: "Persönlich",
      rows: [
        ["Mitarbeiter-Nr.", emp.employee_number],
        ["Vorname", emp.first_name], ["Nachname", emp.last_name],
        ["Geburtsdatum", emp.birth_date], ["Geburtsort", emp.birth_place],
        ["Staatsbürgerschaft", emp.citizenship],
        ["SV-Nummer", emp.sv_number],
        ["Krankenkasse", emp.health_insurance],
      ],
    },
    {
      title: "Kontakt",
      rows: [
        ["Mobil", emp.phone], ["Persönliche E-Mail", emp.private_email],
        ["Firmen-E-Mail", emp.email],
        ["Adresse", [emp.address, emp.address_extra].filter(Boolean).join(", ")],
        ["PLZ / Ort", [emp.zip_code, emp.city].filter(Boolean).join(" ")],
        ["Land", emp.country],
      ],
    },
    {
      title: "Bank",
      rows: [
        ["Bank", emp.bank_name], ["IBAN", emp.iban], ["BIC", emp.bic],
      ],
    },
    {
      title: "Führerschein",
      rows: [
        ["Führerschein-Nr.", emp.driver_license_number],
        ["Ausgestellt von", emp.driver_license_authority],
        ["Gruppe(n)", emp.driver_license_groups || "B"],
        ["Gültig bis", emp.driver_license_expiry],
      ],
    },
    {
      title: "Aufenthaltstitel",
      rows: [
        ["Dokumentenart", emp.residence_doc_type],
        ["Arbeitserlaubnis", emp.work_permit],
        ["Behörde", emp.residence_authority],
        ["Dokument-Nr.", emp.residence_doc_number],
        ["Ausstellungsdatum", emp.residence_issue_date],
        ["Gültig bis", emp.residence_valid_until],
      ],
    },
    {
      title: "Notfallkontakt",
      rows: [
        ["Name", emp.emergency_contact_name], ["Telefon", emp.emergency_contact_phone],
        ["Verhältnis", emp.emergency_contact_relation],
      ],
    },
    {
      title: "Beschäftigung",
      rows: [
        ["Rolle", emp.role], ["Status", emp.status], ["Abteilung", emp.department],
        ["Carrier / DSP", emp.carrier ? emp.carrier.toUpperCase() : "—"],
        ["Standard-Einsatzort", emp.default_workplace],
        ["Wochenstunden", emp.weekly_hours], ["Tägliche Normalarbeitszeit", emp.daily_normal_hours],
        ["Anmeldedatum", emp.employment_start], ["Abmeldedatum", emp.employment_end],
      ],
    },
    {
      title: "Lohn-Abrechnung",
      rows: [
        ["Lenkprotokoll-Art", emp.driving_log_type],
        ["Zertifikat", emp.certificate],
        ["Gesamtlohn berechnen", emp.calc_total_salary ? "Ja" : "Nein"],
        ["Lenkprotokoll nach Fahrzeugtyp", emp.driving_log_by_vehicle_type ? "Ja" : "Nein"],
      ],
    },
  ];
  return (
    <div className="space-y-5" data-testid="stammdaten-tab">
      {canEdit && (
        <div className="flex justify-end">
          <button
            data-testid="stammdaten-edit-btn"
            onClick={openEdit}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
          >
            <Pencil className="w-4 h-4" /> Stammdaten bearbeiten
          </button>
        </div>
      )}
      {sections.map((s) => (
        <div key={s.title} className="bg-zinc-900 border border-zinc-800 rounded-md">
          <div className="px-5 py-3 border-b border-zinc-800 text-sm font-semibold text-zinc-100">{s.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-zinc-800">
            {s.rows.map(([k, v]) => (
              <div key={k} className="px-5 py-3 border-b border-zinc-800 last:border-0 md:[&:nth-last-child(-n+2)]:border-b-0 md:odd:border-r">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                <div className="text-sm text-zinc-100 mt-0.5 font-mono">{(v ?? "") !== "" ? v : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <EmployeeFormWizard
          form={form}
          setForm={setForm}
          editing={true}
          onClose={() => setEditing(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function NotizenTab({ emp, onChange, canEdit }) {
  const [text, setText] = useState(emp.notes || "");
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.put(`/employees/${emp.id}`, { ...emp, notes: text });
      toast.success("Gespeichert");
      onChange?.();
    } catch { toast.error("Fehler"); }
    setSaving(false);
  }
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="notizen-tab">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Interne Notizen zum Mitarbeiter</div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        readOnly={!canEdit}
        placeholder={canEdit ? "Notizen, Bemerkungen, interne Hinweise…" : "Keine Bearbeitungsberechtigung"}
        className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none disabled:opacity-60"
        data-testid="notizen-textarea"
      />
      {canEdit && (
        <div className="mt-3 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="notizen-save" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
}

function ContractsTab({ contracts, empId }) {
  if (!contracts.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded p-8 text-center text-zinc-500 text-sm" data-testid="contracts-empty">
        <div className="mb-2">Keine Verträge erfasst</div>
        <Link to={`/dokumente?owner_type=employee&owner_id=${empId}`} className="text-blue-400 hover:text-blue-300 text-xs">Dokument hochladen →</Link>
      </div>
    );
  }
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto" data-testid="contracts-tab">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <th className="px-4 py-3 text-left">Typ</th>
            <th className="px-4 py-3 text-left">Bezeichnung</th>
            <th className="px-4 py-3 text-left">Hochgeladen</th>
            <th className="px-4 py-3 text-left">Ablauf</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c, i) => (
            <tr key={c.id || i} className={`border-b border-zinc-800/60 ${i % 2 === 0 ? "bg-zinc-900/40" : "bg-zinc-900"}`}>
              <td className="px-4 py-2.5 text-zinc-300">{c.doc_type}</td>
              <td className="px-4 py-2.5 text-zinc-100 font-medium">{c.subject}</td>
              <td className="px-4 py-2.5 text-zinc-500 font-mono text-xs">{fmtDate(c.created_at)}</td>
              <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs">{fmtDate(c.expiry_date)}</td>
              <td className="px-4 py-2.5"><StatusBadge status={c.doc_status}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlySummaryTab({ empId, canExport }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/employees/${empId}/monthly-hours`, { params: { year, month } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [empId, year, month]);

  const fmtH = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;

  return (
    <div data-testid="monthly-summary-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Zeitraum</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" data-testid="monthly-year">
            {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" data-testid="monthly-month">
            {["Jänner","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
        </div>
      </div>

      {!data ? <div className="text-zinc-500 text-sm">Lade…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <MStat label="Arbeitstage" value={data.days_worked} hint="tatsächlich gearbeitet"/>
            <MStat label="Arbeitszeit" value={fmtH(data.work_minutes)} hint={`${data.work_hours} Std. gesamt`} tone="success"/>
            <MStat label="Lenkzeit" value={fmtH(data.driving_minutes)} hint={`${data.driving_hours} Std. gesamt`} tone="success"/>
            <MStat label="Gefahren KM" value={(data.total_km || 0).toLocaleString("de-AT")} hint="gesamt"/>
            <MStat label="Ø Arbeitszeit / Tag" value={fmtH(data.avg_work_minutes_per_day)} hint="bei Arbeitstagen"/>
            <MStat label="Überstunden" value={fmtH(data.overtime_minutes)} hint="> 8h pro Tag" tone={data.overtime_minutes > 0 ? "warning" : "default"}/>
            <MStat label="Bonus" value={fmtCur(data.bonus_total)} hint="Summe im Zeitraum" tone="success"/>
            <MStat label="Abzug / Strafe" value={fmtCur(data.penalty_total)} hint="Summe im Zeitraum" tone={(data.penalty_total || 0) > 0 ? "critical" : "default"}/>
          </div>

          {data.entries?.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="px-4 py-3 text-left">Datum</th>
                    <th className="px-4 py-3 text-left">Von</th>
                    <th className="px-4 py-3 text-left">Bis</th>
                    <th className="px-4 py-3 text-right">Arbeit</th>
                    <th className="px-4 py-3 text-right">Lenkzeit</th>
                    <th className="px-4 py-3 text-right">KM</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr key={e.id || `entry-${i}`} className={`border-b border-zinc-800/60 ${i % 2 === 0 ? "bg-zinc-900/40" : "bg-zinc-900"}`}>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-300">{fmtDate(e.date)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-300">{e.work_start || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-300">{e.work_end || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-zinc-100">{fmtH(e.work_minutes)}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtH(e.driving_minutes)}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-zinc-300">{(e.total_km || 0).toLocaleString("de-AT")}</td>
                      <td className="px-4 py-2"><StatusBadge status={e.status}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MStat({ label, value, hint, tone = "default" }) {
  const colors = { default: "text-zinc-100", success: "text-emerald-400", warning: "text-amber-400", critical: "text-red-400" };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-2xl font-mono font-bold tabular-nums mt-2 ${colors[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function TimelineTab({ owner, id }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/${owner}/${id}/timeline`).then(({ data }) => { setEvents(data.events || []); setLoading(false); });
  }, [owner, id]);
  if (loading) return <div className="text-zinc-500 text-sm">Lade Verlauf...</div>;
  if (events.length === 0) return <EmptyState label="Keine Ereignisse" />;

  const kindStyle = {
    time_entry: { dot: "bg-blue-500", label: "Zeiteintrag" },
    driving_log: { dot: "bg-blue-400", label: "Lenkprotokoll" },
    shift: { dot: "bg-purple-400", label: "Dienstplan" },
    handover: { dot: "bg-emerald-500", label: "Übergabe" },
    bonus: { dot: "bg-emerald-400", label: "Bonus" },
    strafe: { dot: "bg-red-500", label: "Strafe" },
    abzug: { dot: "bg-red-400", label: "Abzug" },
    damage: { dot: "bg-red-500", label: "Schaden" },
    document: { dot: "bg-amber-500", label: "Dokument" },
  };

  // Group by month
  const groups = {};
  events.forEach((e) => {
    const k = (e.ts || "").slice(0, 7);
    if (!groups[k]) groups[k] = [];
    groups[k].push(e);
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="timeline-tab">
      {Object.entries(groups).map(([month, items]) => (
        <div key={month} className="mb-6 last:mb-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3 pb-1 border-b border-zinc-800">
            {new Date(month + "-01").toLocaleDateString("de-AT", { year: "numeric", month: "long" })}
          </div>
          <div className="space-y-3 relative pl-4 border-l border-zinc-800">
            {items.map((e, i) => {
              const s = kindStyle[e.kind] || { dot: "bg-zinc-500", label: e.kind };
              return (
                <div key={e.id || `${e.kind}-${e.ts}-${i}`} className="relative">
                  <div className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full ${s.dot} ring-4 ring-zinc-900`}></div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs text-zinc-500 font-mono tabular-nums shrink-0">{e.ts}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-semibold">{s.label}</span>
                    {e.status && <StatusBadge status={e.status}/>}
                  </div>
                  <div className="text-sm text-zinc-100 mt-0.5">{e.title}</div>
                  {e.meta && <div className="text-xs text-zinc-500 mt-0.5 font-mono">{e.meta}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniTable({ rows, columns }) {
  if (!rows?.length) return <EmptyState label="Keine Einträge" />;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            {columns.map(([k,l]) => <th key={k} className="px-4 py-3 text-left font-semibold">{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-zinc-800 hover:bg-zinc-800/40">
              {columns.map(([k]) => (
                <td key={k} className="px-4 py-2.5 text-zinc-300 font-mono text-xs">
                  {k === "status" || k === "doc_status" ? <StatusBadge status={r[k]} /> : (r[k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BonusTab({ employeeId, items, onChange, defaultType, canEdit, canDelete, canCreate }) {
  const EMPTY = { employee_id: employeeId, type: defaultType, date: new Date().toISOString().slice(0,10), amount: 0, reason: "", status: "offen", notes: "" };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState({});  // id → bool
  const [attachCounts, setAttachCounts] = useState({});  // id → count

  // Load attachment counts for all rows
  useEffect(() => {
    let alive = true;
    (async () => {
      const counts = {};
      await Promise.all(items.map(async (b) => {
        try {
          const { data } = await api.get("/documents", { params: { owner_type: "bonus", owner_id: b.id } });
          counts[b.id] = data.length;
        } catch { counts[b.id] = 0; }
      }));
      if (alive) setAttachCounts(counts);
    })();
    return () => { alive = false; };
  }, [items]);

  async function save() {
    try {
      const payload = { ...form, amount: +form.amount||0 };
      if (editing) await api.put(`/bonuses/${editing.id}`, payload);
      else await api.post("/bonuses", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); onChange();
    } catch { toast.error("Fehler"); }
  }
  async function remove(bid) { if (!window.confirm("Wirklich löschen?")) return; await api.delete(`/bonuses/${bid}`); toast.success("Gelöscht"); onChange(); }

  const title = defaultType === "strafe" ? "Strafe" : "Bonus / Abzug";

  return (
    <div>
      <div className="flex justify-end mb-3">
        {canCreate && (
          <button data-testid={`add-${defaultType}-btn`} onClick={()=>{ setEditing(null); setForm({ ...EMPTY, type: defaultType }); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5"/> {title}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <EmptyState label="Keine Einträge" />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">Datum</th><th className="px-4 py-3 text-left">Typ</th>
              <th className="px-4 py-3 text-left">Grund</th><th className="px-4 py-3 text-right">Betrag</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(b => (
              <Fragment key={b.id}>
                <tr className="border-b border-zinc-800 hover:bg-zinc-800/40" data-testid={`bonus-row-${b.id}`}>
                  <td className="px-4 py-2.5">
                    <button onClick={() => setExpanded(e => ({ ...e, [b.id]: !e[b.id] }))}
                      data-testid={`bonus-expand-${b.id}`}
                      className="text-zinc-400 hover:text-blue-400 inline-flex items-center gap-0.5 text-xs">
                      {expanded[b.id] ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                      <Paperclip className="w-3 h-3"/>
                      <span className="font-mono">{attachCounts[b.id] ?? "·"}</span>
                    </button>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(b.date)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={b.type === "bonus" ? "ok" : b.type === "strafe" ? "kritisch" : "warnung"}/><span className="ml-2 text-zinc-300 text-xs capitalize">{b.type}</span></td>
                  <td className="px-4 py-2.5 text-zinc-100">{b.reason}</td>
                  <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${b.type === "bonus" ? "text-emerald-400" : "text-red-400"}`}>{b.type === "bonus" ? "+" : "-"} {fmtCur(b.amount)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={b.status}/></td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit && <button onClick={()=>{ setEditing(b); setForm({...EMPTY, ...b}); setShowForm(true); }} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5"/></button>}
                    {canDelete && <button onClick={()=>remove(b.id)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                    {!canEdit && !canDelete && <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                </tr>
                {expanded[b.id] && (
                  <tr className="bg-zinc-950/60">
                    <td colSpan={7} className="px-6 py-4 border-b border-zinc-800">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Belege & Anhänge zu „{b.reason}"</div>
                      <EmbeddedDocuments
                        ownerType="bonus"
                        ownerId={b.id}
                        ownerLabel={b.reason}
                        title="Beleg"
                        onChange={() => api.get("/documents", { params: { owner_type: "bonus", owner_id: b.id } }).then(({ data }) => setAttachCounts(c => ({ ...c, [b.id]: data.length })))}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">{editing?`${title} bearbeiten`:`Neue${defaultType==="strafe" ? " Strafe" : "r Bonus"}`}</h3>
              <button onClick={()=>setShowForm(false)} className="text-zinc-400"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div><label className="text-[10px] uppercase text-zinc-500">Datum</label>
                <input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Typ</label>
                <select value={form.type} onChange={e=>setForm({...form, type:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                  <option value="bonus">Bonus</option><option value="abzug">Abzug</option><option value="strafe">Strafe</option>
                </select></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Grund *</label>
                <input value={form.reason} onChange={e=>setForm({...form, reason:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Betrag €</label>
                <input type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Status</label>
                <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                  <option value="offen">offen</option><option value="abgerechnet">abgerechnet</option>
                </select></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Notizen</label>
                <textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="bonus-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================
// KartenTab — Aktuelle Karten (Begeh + Tank) aus der unified Karten-Verwaltung
//
// Read-only Anzeige: zeigt nur die dem Mitarbeiter aktuell zugewiesenen Karten
// (Status = "assigned"). Verwaltung (Anlage, Zuweisung, Statuswechsel, Verlauf)
// läuft zentral über die /karten Seite.
// ============================================================
function KartenTab({ empId, empLabel }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/cards", { params: { employee_id: empId, status: "assigned" } });
      setCards(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { if (empId) reload(); /* eslint-disable-next-line */ }, [empId]);

  const begeh = cards.filter((c) => c.type === "begeh");
  const tank = cards.filter((c) => c.type === "tank");

  return (
    <div className="space-y-5" data-testid="aktuelle-karten-tab">
      <div className="flex items-start justify-between gap-3 bg-blue-500/5 border border-blue-500/20 rounded-md p-4">
        <div className="text-xs text-zinc-300 leading-relaxed">
          Hier siehst du alle Karten, die <strong className="text-zinc-100">{empLabel || "diesem Mitarbeiter"}</strong> aktuell ausgehändigt sind.
          Anlage, Zuweisung, Statuswechsel und vollständiger Verlauf werden zentral in der <Link to="/karten" className="text-blue-300 hover:text-blue-200 underline">Karten-Verwaltung</Link> verwaltet.
        </div>
        <Link to="/karten"
          data-testid="aktuelle-karten-manage-link"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded">
          <KeyRound className="w-3.5 h-3.5" /> Karten verwalten
        </Link>
      </div>

      <CardKindSection
        kind="begeh"
        title="Begehkarten"
        subtitle="Zutritts-/Begehkarten für Lager und Standorte"
        icon={KeyRound}
        items={begeh}
        loading={loading}
      />
      <CardKindSection
        kind="tank"
        title="Tankkarten"
        subtitle="Tankkarten der Tankstellen-Anbieter (DKV, OMV, Shell …)"
        icon={CreditCard}
        items={tank}
        loading={loading}
      />
    </div>
  );
}

function CardKindSection({ kind, title, subtitle, icon: Icon, items, loading }) {
  const isTank = kind === "tank";
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid={`aktuelle-karten-${kind}`}>
      <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
        <Icon className="w-5 h-5 text-amber-400" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-zinc-100">{title}</h4>
          <p className="text-xs text-zinc-500">{subtitle}</p>
        </div>
        <span className="text-xs text-zinc-500" data-testid={`aktuelle-karten-${kind}-count`}>
          {items.length} Karte{items.length === 1 ? "" : "n"}
        </span>
      </div>
      {loading ? (
        <div className="p-4 text-zinc-500 text-sm">Lade…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm italic">Keine Karten zugewiesen</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2 text-left">Nummer</th>
              <th className="px-4 py-2 text-left">Bezeichnung</th>
              {isTank && <th className="px-4 py-2 text-left">Anbieter</th>}
              {isTank && <th className="px-4 py-2 text-left">Gültig bis</th>}
              <th className="px-4 py-2 text-left">Notizen</th>
              <th className="px-4 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-800 hover:bg-zinc-800/40" data-testid={`aktuelle-karten-row-${c.id}`}>
                <td className="px-4 py-2 font-mono text-xs text-zinc-200">{c.card_number}</td>
                <td className="px-4 py-2 text-zinc-300">{c.label || "—"}</td>
                {isTank && <td className="px-4 py-2 text-zinc-400 text-xs">{c.issuer || "—"}</td>}
                {isTank && <td className="px-4 py-2 font-mono text-xs text-zinc-400">{fmtDate(c.expiry_date)}</td>}
                <td className="px-4 py-2 text-zinc-500 text-xs">{c.notes || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Link to="/karten"
                    data-testid={`aktuelle-karten-open-${c.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-zinc-800 hover:bg-amber-600 hover:text-white text-zinc-200 border border-zinc-700 rounded transition-colors">
                    Öffnen →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
// FahrzeughistorieTab — Übergaben aus Fahrer-Sicht (read + create)
// ============================================================
function FahrzeughistorieTab({ empId, emp, handovers, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [cardsPreview, setCardsPreview] = useState([]);
  const [expanded, setExpanded] = useState({});
  const empty = {
    vehicle_id: "", employee_id: empId, date: new Date().toISOString().slice(0, 10),
    direction: "uebergabe", km: 0, fuel_percent: 100, adblue_percent: 100,
    damages_noted: "", checklist_id: "", notes: "",
    signature: "", signature_data_url: "",
  };
  const [form, setForm] = useState(empty);
  const [lastKm, setLastKm] = useState(null);
  const [photos, setPhotos] = useState([]);  // files chosen before handover saved

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/checklists").then(({ data }) => setChecklists(data || []));
  }, []);
  useEffect(() => {
    if (showForm && empId) {
      api.get("/handovers/cards-preview", { params: { employee_id: empId } })
        .then(({ data }) => setCardsPreview(data));
    }
  }, [showForm, empId]);
  useEffect(() => {
    if (form.vehicle_id) {
      api.get("/handovers/last-km", { params: { vehicle_id: form.vehicle_id } })
        .then(({ data }) => setLastKm(data.last_km || 0)).catch(() => setLastKm(null));
    } else setLastKm(null);
  }, [form.vehicle_id]);

  async function save() {
    if (!form.vehicle_id) { toast.error("Fahrzeug auswählen"); return; }
    if (lastKm !== null && +form.km < lastKm) {
      if (!window.confirm(`Kilometerstand ${(+form.km).toLocaleString()} < letzter Stand ${lastKm.toLocaleString()} km.\n\nBist du sicher?`)) return;
    }
    try {
      const { data: ho } = await api.post("/handovers", form);
      // upload any staged photos
      if (photos.length > 0) {
        for (const f of photos) {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/handovers/${ho.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      toast.success("Übergabe gespeichert");
      setShowForm(false); setForm(empty); setPhotos([]);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  async function downloadPdf(hid, date) {
    try {
      const res = await api.get(`/handovers/${hid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Uebergabe_${date}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF-Download fehlgeschlagen"); }
  }

  function vehicleLabel(vid) {
    const v = vehicles.find(x => x.id === vid);
    return v ? `${v.license_plate} — ${v.brand} ${v.model}` : vid;
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setShowForm(true)} data-testid="add-handover-emp-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
          <Truck className="w-3.5 h-3.5"/> Übergabe / Übernahme
        </button>
      </div>
      {handovers.length === 0 ? (
        <EmptyState label="Keine Übergaben erfasst" />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">Datum</th><th className="px-4 py-3 text-left">Fahrzeug</th>
              <th className="px-4 py-3 text-left">Richtung</th><th className="px-4 py-3 text-right">KM</th>
              <th className="px-4 py-3 text-right">Tank %</th><th className="px-4 py-3">Karten</th>
              <th className="px-4 py-3 text-right">PDF</th>
            </tr></thead>
            <tbody>{handovers.map(h => {
              const cards = h.cards_snapshot || [];
              return (
                <Fragment key={h.id}>
                  <tr className="border-b border-zinc-800 hover:bg-zinc-800/40">
                    <td className="px-4 py-2.5">
                      {cards.length > 0 ? (
                        <button onClick={() => setExpanded(e => ({ ...e, [h.id]: !e[h.id] }))}
                          data-testid={`handover-cards-toggle-${h.id}`}
                          className="text-zinc-400 hover:text-blue-400">
                          {expanded[h.id] ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                        </button>
                      ) : <span className="text-zinc-700 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(h.date)}</td>
                    <td className="px-4 py-2.5 text-zinc-100">{vehicleLabel(h.vehicle_id)}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${h.direction === "uebergabe" ? "bg-blue-500/20 text-blue-300" : "bg-amber-500/20 text-amber-300"}`}>{h.direction === "uebergabe" ? "Übergabe →" : "← Übernahme"}</span></td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-300">{h.km?.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-300">{h.fuel_percent}%</td>
                    <td className="px-4 py-2.5">
                      {cards.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-300">
                          <CreditCard className="w-3 h-3"/> {cards.length}
                        </span>
                      ) : <span className="text-zinc-700 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => downloadPdf(h.id, h.date)} data-testid={`handover-pdf-${h.id}`}
                        className="p-1.5 text-zinc-400 hover:text-blue-400" title="Übergabeprotokoll PDF">
                        <FileDown className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                  {expanded[h.id] && cards.length > 0 && (
                    <tr className="bg-zinc-950/60">
                      <td colSpan={8} className="px-6 py-3 border-b border-zinc-800">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                          Übergebene Karten ({cards.length}) — Snapshot zum Übergabezeitpunkt
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {cards.map(c => (
                            <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded p-2.5 text-xs flex items-center gap-2" data-testid={`handover-card-${h.id}-${c.id}`}>
                              {c.kind === "tank" ? <CreditCard className="w-3.5 h-3.5 text-blue-400"/> : <KeyRound className="w-3.5 h-3.5 text-amber-400"/>}
                              <div className="flex-1 min-w-0">
                                <div className="text-zinc-100 font-medium truncate">
                                  {c.provider || c.label || "—"}
                                  {c.label && c.provider ? <span className="text-zinc-500 font-normal"> · {c.label}</span> : null}
                                </div>
                                <div className="text-zinc-500 font-mono text-[10px]">
                                  {c.card_number || "ohne Nr."}{c.expiry_date ? ` · gültig bis ${c.expiry_date}` : ""}
                                </div>
                              </div>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.kind === "tank" ? "bg-blue-500/20 text-blue-300" : "bg-amber-500/20 text-amber-300"}`}>
                                {c.kind === "tank" ? "Tankkarte" : "BG-Karte"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">Neue Übergabe / Übernahme<span className="ml-2 text-xs text-zinc-500 font-normal">· {emp.first_name} {emp.last_name}</span></h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500">Fahrzeug *</label>
                <div className="mt-1">
                  <VehiclePicker value={form.vehicle_id} testid="profile-handover-veh-picker"
                    onChange={(v) => setForm({ ...form, vehicle_id: v })} />
                </div>
              </div>
              <div><label className="text-[10px] uppercase text-zinc-500">Datum</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Richtung</label>
                <select value={form.direction} onChange={e => setForm({ ...form, direction: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                  <option value="uebergabe">Übergabe an Fahrer</option>
                  <option value="uebernahme">Übernahme vom Fahrer</option>
                </select></div>
              <div><label className="text-[10px] uppercase text-zinc-500">KM-Stand</label>
                <input type="number" value={form.km} onChange={e => setForm({ ...form, km: +e.target.value })}
                  data-testid="handover-km-input"
                  className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${lastKm !== null && +form.km < lastKm ? "border-red-500/60" : "border-zinc-800"}`}/>
                {lastKm !== null && (
                  <div className={`mt-1 text-[10px] flex items-center gap-1 ${+form.km < lastKm ? "text-red-400" : "text-zinc-500"}`} data-testid="km-plausibility">
                    {+form.km < lastKm && <AlertCircle className="w-3 h-3"/>}
                    Letzter KM-Stand: {lastKm.toLocaleString()} km
                  </div>
                )}
              </div>
              <div><label className="text-[10px] uppercase text-zinc-500">Tank %</label>
                <input type="number" min="0" max="100" value={form.fuel_percent} onChange={e => setForm({ ...form, fuel_percent: +e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              {checklists.length > 0 && (
                <div className="col-span-2">
                  <label className="text-[10px] uppercase text-zinc-500">Checkliste</label>
                  <select value={form.checklist_id} onChange={e => setForm({ ...form, checklist_id: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    <option value="">— keine —</option>
                    {checklists.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500">Schäden / Besonderheiten</label>
                <input value={form.damages_noted} onChange={e => setForm({ ...form, damages_noted: e.target.value })}
                  placeholder="z.B. Kratzer vorne links, Stoßstange beschädigt"
                  data-testid="handover-damages-input"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500 flex items-center justify-between">
                  <span>Fotos vom Fahrzeugzustand (optional)</span>
                  <span className="text-[10px] text-zinc-500 normal-case">{photos.length} ausgewählt</span>
                </label>
                <input type="file" multiple accept="image/*"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    // Phase 5Z.42 — compress all photos before storing in state
                    const compressed = await Promise.all(files.map((f) => compressImage(f, { maxDimension: 1920, jpegQuality: 0.85 })));
                    setPhotos(compressed);
                  }}
                  data-testid="handover-photos-input"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:text-xs"/>
                {photos.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {photos.map((f, i) => (
                      <div key={`${f.name}-${f.lastModified}-${i}`} className="w-14 h-14 rounded border border-zinc-700 overflow-hidden relative">
                        <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover"/>
                        <button type="button" onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                          className="absolute top-0 right-0 bg-red-600 text-white w-4 h-4 flex items-center justify-center text-[10px]">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Notizen</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              <div className="col-span-2">
                <SignatureCanvas
                  value={form.signature_data_url}
                  onChange={(url) => setForm({ ...form, signature_data_url: url, signature: `${emp.first_name} ${emp.last_name}` })}
                  label="Unterschrift Fahrer (mit Maus / Touch)"
                  testid="handover-signature"
                />
              </div>
              {cardsPreview.length > 0 && (
                <div className="col-span-2 mt-2 bg-blue-950/30 border border-blue-900/40 rounded p-3" data-testid="handover-cards-preview">
                  <div className="text-[10px] uppercase tracking-wider text-blue-300 mb-2 flex items-center gap-1.5">
                    <CreditCard className="w-3 h-3"/>
                    Diese {cardsPreview.length} Karte{cardsPreview.length === 1 ? "" : "n"} werden automatisch protokolliert
                  </div>
                  <div className="space-y-1">
                    {cardsPreview.map(c => (
                      <div key={c.id} className="flex items-center gap-2 text-xs">
                        {c.kind === "tank" ? <CreditCard className="w-3 h-3 text-blue-400"/> : <KeyRound className="w-3 h-3 text-amber-400"/>}
                        <span className="text-zinc-100">{c.provider || c.label || "—"}</span>
                        {c.label && c.provider && <span className="text-zinc-500">· {c.label}</span>}
                        <span className="text-zinc-500 font-mono text-[10px] ml-auto">{c.card_number || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="handover-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ExportableMiniTable — MiniTable + PDF/CSV download buttons
// ============================================================
function ExportableMiniTable({ rows, columns, empId, emp, reportKind, reportLabel, canExport }) {
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const [from, setFrom] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));

  async function download(format) {
    setBusy(true);
    try {
      const url = `/employees/${empId}/${reportKind}-report.${format}`;
      const res = await api.get(url, { params: { from, to }, responseType: "blob" });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = link;
      a.download = `${reportLabel}_${emp.first_name}-${emp.last_name}_${from}_${to}.${format}`;
      a.click(); URL.revokeObjectURL(link);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="text-xs text-zinc-500">Export-Zeitraum:</div>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <span className="text-zinc-600">→</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("pdf")} disabled={busy || !canExport}
            data-testid={`download-${reportKind}-pdf`}
            title={!canExport ? "Keine Export-Berechtigung" : ""}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white text-xs font-medium rounded">
            <FileDown className="w-3.5 h-3.5"/> PDF
          </button>
          <button onClick={() => download("csv")} disabled={busy || !canExport}
            data-testid={`download-${reportKind}-csv`}
            title={!canExport ? "Keine Export-Berechtigung" : ""}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-100 text-xs font-medium rounded border border-zinc-700">
            <FileDown className="w-3.5 h-3.5"/> CSV
          </button>
        </div>
      </div>
      <MiniTable rows={rows} columns={columns}/>
    </div>
  );
}


// ============================================================
// KalenderTab — Monatsansicht mit Schichten, Zeiteinträgen, Übergaben
// ============================================================
function KalenderTab({ empId, shifts, timeEntries, handovers, onRequestAbsence }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() + 1 });

  const first = new Date(cursor.y, cursor.m - 1, 1);
  const daysInMonth = new Date(cursor.y, cursor.m, 0).getDate();
  // Monday-first offset (0 = Mon … 6 = Sun)
  const offset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.y}-${String(cursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const shift = shifts.find(s => s.date === iso);
    const time = timeEntries.find(t => t.date === iso);
    const ho = handovers.filter(h => h.date === iso);
    cells.push({ d, iso, shift, time, ho });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  function shiftStyle(type) {
    const map = {
      arbeit: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40", label: "Arbeit" },
      frei: { bg: "bg-zinc-500/15", text: "text-zinc-400", border: "border-zinc-600/40", label: "Frei" },
      urlaub: { bg: "bg-blue-500/15", text: "text-blue-300", border: "border-blue-500/40", label: "Urlaub" },
      krank: { bg: "bg-red-500/15", text: "text-red-300", border: "border-red-500/40", label: "Krank" },
      schulung: { bg: "bg-purple-500/15", text: "text-purple-300", border: "border-purple-500/40", label: "Schulung" },
    };
    return map[type] || { bg: "bg-zinc-800/50", text: "text-zinc-300", border: "border-zinc-700", label: type };
  }

  function prev() { const m = cursor.m === 1 ? 12 : cursor.m - 1; const y = cursor.m === 1 ? cursor.y - 1 : cursor.y; setCursor({ y, m }); }
  function next() { const m = cursor.m === 12 ? 1 : cursor.m + 1; const y = cursor.m === 12 ? cursor.y + 1 : cursor.y; setCursor({ y, m }); }
  function todayJump() { setCursor({ y: today.getFullYear(), m: today.getMonth() + 1 }); }

  const monthLabel = new Date(cursor.y, cursor.m - 1, 1)
    .toLocaleDateString("de-AT", { year: "numeric", month: "long" });
  const todayIso = today.toISOString().slice(0, 10);

  // Summary counts for legend
  const counts = { arbeit: 0, frei: 0, urlaub: 0, krank: 0, schulung: 0 };
  shifts.forEach(s => {
    if (s.date.startsWith(`${cursor.y}-${String(cursor.m).padStart(2, "0")}`) && counts.hasOwnProperty(s.shift_type)) counts[s.shift_type] += 1;
  });

  return (
    <div data-testid="kalender-tab">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={prev} data-testid="kalender-prev" className="px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-zinc-300 hover:bg-zinc-800 text-xs"><ChevronRight className="w-4 h-4 rotate-180"/></button>
        <div className="font-display font-semibold text-zinc-100 text-lg capitalize" data-testid="kalender-month-label">{monthLabel}</div>
        <button onClick={next} data-testid="kalender-next" className="px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-zinc-300 hover:bg-zinc-800 text-xs"><ChevronRight className="w-4 h-4"/></button>
        <button onClick={todayJump} data-testid="kalender-today" className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-zinc-300 hover:bg-zinc-800 text-xs">Heute</button>
        <button onClick={onRequestAbsence} data-testid="kalender-request-absence" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600/80 hover:bg-amber-600 text-white rounded font-medium">
          <CalendarPlus className="w-3.5 h-3.5"/> Abwesenheit anfragen
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mb-3 flex-wrap text-xs">
        {["arbeit", "frei", "urlaub", "krank", "schulung"].map(k => {
          const s = shiftStyle(k);
          return (
            <span key={k} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border ${s.bg} ${s.text} ${s.border}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current"/>
              {s.label}
              <span className="font-mono tabular-nums opacity-70">{counts[k]}</span>
            </span>
          );
        })}
      </div>

      {/* Grid */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map(d => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-zinc-500 text-center font-semibold py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="min-h-[84px]"/>;
            const isToday = c.iso === todayIso;
            const s = c.shift ? shiftStyle(c.shift.shift_type) : null;
            return (
              <div
                key={i}
                data-testid={`kalender-day-${c.iso}`}
                className={`min-h-[84px] rounded border p-2 text-xs relative transition-colors ${
                  s ? `${s.bg} ${s.border}` : "bg-zinc-950/50 border-zinc-800"
                } ${isToday ? "ring-2 ring-blue-500" : ""}`}
              >
                <div className={`font-mono font-semibold ${isToday ? "text-blue-400" : "text-zinc-200"}`}>{c.d}</div>
                {s && (
                  <div className={`mt-1 text-[10px] font-semibold ${s.text}`}>{s.label}</div>
                )}
                {c.time && c.time.work_minutes > 0 && (
                  <div className="mt-1 text-[10px] text-zinc-300 font-mono">
                    {Math.floor(c.time.work_minutes / 60)}h{c.time.work_minutes % 60 ? `${c.time.work_minutes % 60}m` : ""}
                  </div>
                )}
                {c.ho.length > 0 && (
                  <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] text-amber-300 bg-amber-500/20 px-1 rounded" title={`${c.ho.length} Übergabe(n)`}>
                    <Truck className="w-2.5 h-2.5"/>{c.ho.length}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// AbsenceRequestModal — Krankmeldung / Urlaub / Frei für Zeitraum
// ============================================================
function AbsenceRequestModal({ empId, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    shift_type: "urlaub",
    date_from: today,
    date_to: today,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.date_from || !form.date_to) { toast.error("Zeitraum angeben"); return; }
    if (form.date_to < form.date_from) { toast.error("Enddatum liegt vor Startdatum"); return; }
    setSaving(true);
    try {
      // Build one shift entry per day in the range
      const start = new Date(form.date_from);
      const end = new Date(form.date_to);
      const daysToCreate = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        daysToCreate.push(d.toISOString().slice(0, 10));
      }
      for (const iso of daysToCreate) {
        await api.post("/shifts", {
          date: iso,
          employee_id: empId,
          shift_type: form.shift_type,
          workplace: "",
          notes: form.notes,
        });
      }
      toast.success(`${daysToCreate.length} Tag(e) als „${form.shift_type}" eingetragen`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="absence-request-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-amber-400"/> Abwesenheit anfragen
          </h3>
          <button onClick={onClose} className="text-zinc-400" data-testid="absence-close"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Art</label>
            <select
              value={form.shift_type}
              onChange={(e) => setForm({ ...form, shift_type: e.target.value })}
              data-testid="absence-type"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
            >
              <option value="urlaub">Urlaub</option>
              <option value="krank">Krankmeldung</option>
              <option value="frei">Frei (unbezahlt)</option>
              <option value="schulung">Schulung</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Von</label>
              <input type="date" value={form.date_from}
                onChange={(e) => setForm({ ...form, date_from: e.target.value, date_to: form.date_to < e.target.value ? e.target.value : form.date_to })}
                data-testid="absence-from"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Bis</label>
              <input type="date" value={form.date_to}
                onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                data-testid="absence-to"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Grund / Notiz</label>
            <textarea rows={3} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={form.shift_type === "krank" ? "z.B. Grippe, AU-Bescheinigung folgt" : "Optional"}
              data-testid="absence-notes"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>
          {form.date_from && form.date_to && (
            <div className="text-[11px] text-zinc-500 bg-zinc-950/60 border border-zinc-800 rounded px-3 py-2">
              Zeitraum: <span className="text-zinc-200 font-mono">{form.date_from}</span> bis <span className="text-zinc-200 font-mono">{form.date_to}</span>
              {" — "}
              {Math.max(1, Math.round((new Date(form.date_to) - new Date(form.date_from)) / 86400000) + 1)} Tag(e)
            </div>
          )}
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="absence-save"
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : "Beantragen"}
          </button>
        </div>
      </div>
    </div>
  );
}
